import { Box, Typography, Divider, useTheme, alpha } from '@mui/material';

export default function SmsAdminSettingInstructions() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mt: 4,
        p: 3,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.grey[500], 0.04),
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Instructions &amp; Guidelines
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Please review the following instructions before submitting any changes:
      </Typography>

      <Divider sx={{ mb: 2.5 }} />

      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.8,
          '& li': {
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: 'text.secondary',
          },
          '& strong': {
            color: 'text.primary',
          },
        }}
      >
        <li>
          <strong>User &amp; Campaign Ownership:</strong> Select the User first. All entered Campaign IDs must belong to the selected User. If any campaign does not match the user, the entire request will be rejected. Multiple Campaign IDs can be entered separated by commas or spaces.
        </li>

        <li>
          <strong>Count vs. Percentage (%) Mode:</strong>
          <Box component="ul" sx={{ pl: 2, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <li><strong>Count:</strong> Applies the exact numeric count specified.</li>
            <li><strong>Percent (%):</strong> Calculates target count as a percentage of each campaign&apos;s total contacts (recommended when updating multiple campaigns with different sizes).</li>
          </Box>
        </li>

        <li>
          <strong>Update Delivered Count (D):</strong> Increases delivered contacts by rebalancing with the selected <em>&apos;Adjusted To&apos;</em> pool (Pending, Failed, DND). If <strong>Pending (P)</strong> is included, unsubmitted contacts are converted to Delivered, which also increases the campaign&apos;s total submitted count.
        </li>

        <li>
          <strong>Update Failed Count (F):</strong> Increases failed contacts using the selected <em>&apos;Adjusted To&apos;</em> pool (Pending, Delivered, DND). Converted failed contacts are automatically assigned realistic operator error codes and undelivered receipts.
        </li>

        <li>
          <strong>Update Submitted Count (S):</strong>
          <Box component="ul" sx={{ pl: 2, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <li>Submitted count can <strong>ONLY be increased</strong> (never decreased) and cannot exceed total campaign contacts.</li>
            <li>Converts unsubmitted pending contacts into submitted status with your specified breakdown across Delivered, Failed, and DND.</li>
          </Box>
        </li>

        <li>
          <strong>Campaign Date &amp; Time:</strong>
          <Box component="ul" sx={{ pl: 2, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <li>Updates campaign creation and start dates in MongoDB.</li>
            <li>Randomizes message submit and delivery dates in Elasticsearch between <em>Message Send Time</em> and <em>Delivery Time</em>.</li>
            <li><strong>Message Send Time must be earlier than or equal to Delivery Time.</strong> Genuine pending in-flight messages and gateway-submitted messages are never modified.</li>
          </Box>
        </li>

        <li>
          <strong>Include All Campaign Records:</strong>
          <Box component="ul" sx={{ pl: 2, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <li><strong>Unchecked (Default):</strong> Only updates records that were already manipulated by admin settings (safe mode). Genuine carrier records remain untouched.</li>
            <li><strong>Checked:</strong> Allows rebalancing across all matching records in the campaign index.</li>
          </Box>
        </li>

        <li>
          <strong>Automated Sync &amp; Invoices:</strong> After processing, campaign counters in MongoDB are automatically refreshed from Elasticsearch, and user invoices (<code>campaign_invoices</code>) are automatically recalculated proportionally based on the new delivered or submitted counts.
        </li>
      </Box>
    </Box>
  );
}
